import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { administrationApi } from '@/services/instanceAdministration'
import { authApi } from '@/services/auth/api'
import { clearAccessToken, clearCurrentUserId, setAccessToken, setCurrentUserId } from '@/services/auth/storage'
import { clearCurrentUserDisplayName } from '@/services/authSession'
import type { ApiError } from '@/services/http/errors'
import type { AccessPhase } from '../contracts'

const ACCESS = ['instance-administration', 'access'] as const
export const ADMIN_DATA = ['instance-administration', 'data'] as const

export const useAccess = () => {
  const client = useQueryClient()
  const [mode, setMode] = useState<'restore' | 'signed_out'>('restore')
  const [loginError, setLoginError] = useState('')
  const request = useRef<AbortController | null>(null)
  const pendingLogin = useRef<Promise<void> | null>(null)
  const resetMutation = useRef(() => {})
  const access = useQuery({ queryKey: ACCESS, queryFn: ({ signal }) => administrationApi.access(signal),
    retry: false, staleTime: Infinity, enabled: mode === 'restore' })
  const authentication = useMutation({
    gcTime: 0,
    mutationFn: async ({ username, credential }: { username: string; credential: string }) => {
      request.current?.abort()
      const controller = new AbortController()
      request.current = controller
      const response = await authApi.exchangeTokenRoot(credential, controller.signal, username)
      controller.signal.throwIfAborted()
      if (typeof response.access_token !== 'string' || typeof response.user_id !== 'string') {
        throw new TypeError('登录响应不可用')
      }
      setCurrentUserId(response.user_id)
      setAccessToken(response.access_token)
      const accepted = await administrationApi.access(controller.signal)
      controller.signal.throwIfAborted()
      return accepted
    },
    onMutate: () => { setLoginError('') },
    onSuccess: async (accepted) => {
      await client.cancelQueries({ queryKey: ADMIN_DATA })
      if (request.current?.signal.aborted) return
      client.removeQueries({ queryKey: ADMIN_DATA })
      client.setQueryData(ACCESS, accepted)
      setMode('restore')
    },
    onError: (error) => { if (!request.current?.signal.aborted) setLoginError(error.message) },
    onSettled: () => { request.current = null; resetMutation.current() },
  })
  const { mutateAsync, reset } = authentication
  useEffect(() => { resetMutation.current = reset }, [reset])
  useEffect(() => () => { request.current?.abort(); void client.cancelQueries() }, [client])
  const status = (access.error as ApiError | null)?.status
  useEffect(() => {
    if (status === 401 || status === 403) {
      void client.cancelQueries({ queryKey: ADMIN_DATA })
      client.removeQueries({ queryKey: ADMIN_DATA })
    }
  }, [client, status])
  const login = useCallback((username: string, credential: string) => {
    if (pendingLogin.current) return pendingLogin.current
    const pending = mutateAsync({ username, credential }).then(() => undefined).catch(() => undefined)
      .finally(() => { pendingLogin.current = null })
    pendingLogin.current = pending
    return pending
  }, [mutateAsync])
  const logout = useCallback(async () => {
    setMode('signed_out')
    request.current?.abort()
    await client.cancelQueries()
    client.removeQueries({ queryKey: ADMIN_DATA })
    client.removeQueries({ queryKey: ACCESS })
    clearAccessToken(); clearCurrentUserId(); clearCurrentUserDisplayName()
    await authApi.logout().catch(() => { setLoginError('退出结果未确认，请重新验证') })
  }, [client])
  const revalidate = useCallback(() => {
    setMode('restore')
    void client.invalidateQueries({ queryKey: ACCESS })
  }, [client])
  const errorPhases: Record<number, AccessPhase> = { 401: 'login', 403: 'denied' }
  const states: Record<string, AccessPhase> = { pending: 'checking', success: 'ready', error: errorPhases[status ?? 0] ?? 'error' }
  const phase: AccessPhase = mode === 'signed_out' ? 'login' : states[access.status]
  return { phase, userId: access.data?.user_id ?? '', login, logout, revalidate,
    pending: authentication.isPending, error: loginError || access.error?.message || '' }
}
